import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { X, Gift, Mail, Sparkles } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { BACKEND_URL as API } from "../../apiConfig";


const EmailCapturePopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    // Check if user has already seen/dismissed the popup
    const hasSeenPopup = localStorage.getItem("email_popup_dismissed");
    const hasSubscribed = localStorage.getItem("email_subscribed");
    
    if (hasSeenPopup || hasSubscribed) return;

    // Show popup after 15 seconds on site
    const timer = setTimeout(() => {
      setIsOpen(true);
    }, 15000);

    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      await axios.post(`${API}/api/newsletter/subscribe`, { email });
      setSubmitted(true);
      localStorage.setItem("email_subscribed", "true");
      toast.success("Welcome to the collection.");
      setTimeout(() => setIsOpen(false), 3000);
    } catch (error) {
      if (error.response?.status === 409) {
        toast.info("You're already subscribed!");
        setIsOpen(false);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    localStorage.setItem("email_popup_dismissed", Date.now().toString());
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", duration: 0.5 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md bg-background rounded-3xl shadow-2xl overflow-hidden"
            data-testid="email-capture-popup"
          >
            {/* Close Button */}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-muted transition-colors z-10"
              data-testid="close-email-popup"
            >
              <X className="h-5 w-5" />
            </button>

            {/* Header Image/Pattern */}
            <div className="h-32 bg-gradient-to-br from-primary/20 via-primary/10 to-background flex items-center justify-center">
              <div className="flex items-center gap-3">
                <Gift className="h-12 w-12 text-primary" />
                <Sparkles className="h-8 w-8 text-primary/60" />
              </div>
            </div>

            {/* Content */}
            <div className="p-8 text-center">
              {!submitted ? (
                <>
                  <h3 className="text-2xl font-bold mb-2">
                    Join Our Private Collection
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    Be the first to see new works, exhibition announcements, and 
                    stories from the studio.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                      <Input
                        type="email"
                        placeholder="Enter your email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-12 py-6 rounded-xl text-base"
                        required
                        data-testid="email-capture-input"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full py-6 rounded-xl text-base"
                      disabled={loading}
                      data-testid="email-capture-submit"
                    >
                      {loading ? "Subscribing..." : "Join the Collection"}
                    </Button>
                  </form>

                  <p className="text-xs text-muted-foreground mt-4">
                    By subscribing, you agree to receive marketing emails. Unsubscribe anytime.
                  </p>
                </>
              ) : (
                <div className="py-4">
                  <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <Sparkles className="h-8 w-8 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold mb-2">Welcome to the Family!</h3>
                  <p className="text-muted-foreground">
                    Welcome. You'll hear from us soon.
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default EmailCapturePopup;
